/**
 * Best-effort UI restrictions for a web app.
 *
 * Important: In a browser you cannot truly "block" screenshots, DevTools from browser menus,
 * or extensions. This only deters common UI actions by intercepting events.
 *
 * Typing is intentionally NOT blocked: we block print/save shortcuts, View Source via keyboard
 * (Ctrl/Cmd+U), F12 (DevTools best-effort), and PrintScreen best-effort. Right-click is not blocked.
 *
 * Opt-out: wrap parts you want to allow in an element with `data-allow-browser-defaults`.
 */

import store from "@/core/services/store";
import { SET_SNACK } from "@/core/services/store/snackbar.module";

const DEFAULT_WARNING =
  "You are not allowed to perform this action.";

let lastNotifyAt = 0;
const NOTIFY_THROTTLE_MS = 800;

function notifyRestricted(message = DEFAULT_WARNING) {
  const now = Date.now();
  if (now - lastNotifyAt < NOTIFY_THROTTLE_MS) return;
  lastNotifyAt = now;
  store.dispatch(SET_SNACK, { msg: message, clr: "error" });
}

function blockAndWarn(e) {
  e.preventDefault();
  notifyRestricted();
}
function isOptOutTarget(target) {
  if (!target || target.nodeType !== 1) return false;
  const el = target;
  return !!(el.closest && el.closest("[data-allow-browser-defaults]"));
}

function onKeyDown(e) {
  if (isOptOutTarget(e.target)) return;

  const key = (e.key || "").toLowerCase();
  const ctrl = e.ctrlKey || e.metaKey;

  // Print / file
  if (ctrl && key === "p") return blockAndWarn(e);
  if (ctrl && (key === "s" || key === "o")) return blockAndWarn(e);

  // View page source (keyboard only): Ctrl+U / Cmd+U / Cmd+Option+U — right-click menu still works
  if (key === "u" && (e.ctrlKey || e.metaKey)) return blockAndWarn(e);

  // DevTools / Inspect (F12 only — menu and Ctrl+Shift+I still work in most browsers)
  if (key === "f12" || e.code === "F12") return blockAndWarn(e);

  // Screenshot key (best-effort; OS may ignore)
  if (e.code === "PrintScreen" || key === "printscreen") return blockAndWarn(e);
}

function onDragStart(e) {
  if (!isOptOutTarget(e.target)) {
    e.preventDefault();
    notifyRestricted();
  }
}

function noopPrint() {
  notifyRestricted();
}

export function initBrowserRestrictions() {
  document.addEventListener("keydown", onKeyDown, true);
  document.addEventListener("dragstart", onDragStart, true);

  window.print = noopPrint;
}
